/**
 * <keystone-field-text> — string input.
 *
 * Maps onto JSON Schema `type: "string"`. Supports:
 *   - format="email"  → renders <input type="email">
 *   - minlength / maxlength
 *   - pattern (HTML pattern attribute)
 *
 * Emits a `keystone-field-input` event (bubbling, composed) on every
 * input. Detail: { name, value }. The parent <keystone-form> listens for
 * this and updates its internal data state.
 */

import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

@customElement("keystone-field-text")
export class KeystoneFieldText extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) format: string | null = null;
  @property({ type: Number }) minlength: number | null = null;
  @property({ type: Number }) maxlength: number | null = null;
  @property({ type: String }) pattern: string | null = null;

  override updated(): void {
    // Toggle the data-error attribute on :host so the shared CSS can
    // recolour the input border. Lit doesn't reflect arbitrary
    // properties onto the host by default.
    if (this.error) {
      this.setAttribute("data-error", "");
    } else {
      this.removeAttribute("data-error");
    }
  }

  private onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: input.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;
    const type = this.format === "email" ? "email" : "text";

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <input
        id=${inputId}
        name=${this.name}
        type=${type}
        .value=${this.value}
        ?required=${this.required}
        ?disabled=${this.disabled}
        minlength=${ifDefined(this.minlength ?? undefined)}
        maxlength=${ifDefined(this.maxlength ?? undefined)}
        pattern=${ifDefined(this.pattern || undefined)}
        aria-invalid=${this.error ? "true" : "false"}
        aria-describedby=${this.error ? `${inputId}-error` : nothing}
        @input=${this.onInput}
      />
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-text": KeystoneFieldText;
  }
}
